import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Video, 
  Zap,
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  ArrowRight,
  Rocket,
  TrendingUp,
  Calendar,
  AlertCircle,
  Target,
  Eye,
  GripVertical,
  Plus,
  Filter,
  Settings,
  MoreHorizontal,
  Edit,
  Trash2,
  Check,
  X,
  ChevronDown
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addDays, startOfWeek, endOfWeek, isToday, isSameDay, addMonths, addWeeks } from 'date-fns';
import { sendRequest } from '../api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { cn } from '@/lib/utils';

// Custom Button Component
const Button = React.forwardRef(({ 
  className, 
  variant = 'default', 
  size = 'default', 
  children,
  theme = 'dark',
  ...props 
}, ref) => {
  const variants = {
    default: theme === 'light' 
      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
      : 'bg-indigo-500 text-white hover:bg-indigo-600',
    outline: theme === 'light'
      ? 'border border-gray-200 hover:bg-gray-50'
      : 'border border-gray-700 hover:bg-gray-800',
    ghost: theme === 'light'
      ? 'hover:bg-gray-100'
      : 'hover:bg-gray-800',
    link: theme === 'light'
      ? 'text-indigo-600 hover:underline'
      : 'text-indigo-400 hover:underline'
  };

  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 py-1 text-sm',
    lg: 'h-12 px-6 py-3 text-lg'
  };

  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});

// Custom Badge Component
const Badge = ({ children, variant = 'default', className, theme = 'light' }) => {
  const variants = theme === 'light' ? {
    default: 'bg-indigo-100 text-indigo-800',
    secondary: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800'
  } : {
    default: 'bg-indigo-900/50 text-indigo-300',
    secondary: 'bg-gray-800 text-gray-300',
    success: 'bg-green-900/50 text-green-300',
    warning: 'bg-yellow-900/50 text-yellow-300',
    error: 'bg-red-900/50 text-red-300'
  };

  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
      variants[variant],
      className
    )}>
      {children}
    </span>
  );
};

// Custom Select Component
const Select = ({ 
  value, 
  onValueChange, 
  options, 
  className,
  theme = 'light',
  placeholder = 'Select option...'
}) => {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(
          'block w-full rounded-md border py-2 pl-3 pr-10 text-sm appearance-none',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500',
          theme === 'light' 
            ? 'border-gray-200 bg-white text-gray-900'
            : 'border-gray-700 bg-gray-800 text-gray-100',
          className
        )}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            className={theme === 'dark' ? 'bg-gray-800' : undefined}
          >
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
        <ChevronDown className={cn(
          "h-4 w-4",
          theme === 'light' ? "text-gray-400" : "text-gray-500"
        )} />
      </div>
    </div>
  );
};

// Custom Switch Component
const Switch = ({ checked, onCheckedChange, className }) => {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
        checked ? 'bg-indigo-600' : 'bg-gray-200',
        className
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
};

// Custom Modal/Dialog Component
const Modal = ({ 
  open, 
  onClose, 
  title, 
  children,
  className,
  theme = 'light'
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 flex items-center justify-center">
        <div 
          className={cn(
            "fixed inset-0 backdrop-blur-sm",
            theme === 'light' ? "bg-black/25" : "bg-black/50"
          )}
          onClick={onClose}
        />
        <div className={cn(
          'relative rounded-lg shadow-xl max-w-md w-full p-6',
          theme === 'light' 
            ? 'bg-white'
            : 'bg-gray-900 border border-gray-800',
          className
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={cn(
              "text-lg font-semibold",
              theme === 'light' ? "text-gray-900" : "text-gray-100"
            )}>
              {title}
            </h2>
            <button
              onClick={onClose}
              className={cn(
                theme === 'light' 
                  ? "text-gray-400 hover:text-gray-500"
                  : "text-gray-500 hover:text-gray-400"
              )}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// Custom Tooltip Component
const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md whitespace-nowrap"
            style={{
              bottom: 'calc(100% + 5px)',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            {content}
            <div
              className="absolute w-2 h-2 bg-gray-900 rotate-45"
              style={{
                bottom: '-4px',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Add after the Tooltip component
const Card = ({ children, className }) => {
  return (
    <div className={cn(
      "rounded-lg border border-white/20 shadow-sm",
      "bg-white/60 backdrop-blur-sm",
      className
    )}>
      {children}
    </div>
  );
};

// Constants
const CALENDAR_VIEWS = {
  WEEK: 'week',
  MONTH: 'month',
  AGENDA: 'agenda'
};

const POST_TYPES = {
  VIDEO: 'video',
  AUTOMATION: 'automation',
  STORY: 'story',
  REEL: 'reel',
  OPLAE_VIDEO: 'oplae_video',
  OPLAE_TASK: 'oplae_task'
};

const TIME_SLOTS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM to 8 PM

const CAMPAIGN_COLORS = {
  blue: { bg: '#60a5fa', text: '#1e40af', light: '#dbeafe' },
  purple: { bg: '#a78bfa', text: '#5b21b6', light: '#ede9fe' },
  pink: { bg: '#f472b6', text: '#9d174d', light: '#fce7f3' },
  green: { bg: '#34d399', text: '#065f46', light: '#d1fae5' },
  orange: { bg: '#fb923c', text: '#9a3412', light: '#ffedd5' },
  yellow: { bg: '#fbbf24', text: '#92400e', light: '#fef3c7' },
  red: { bg: '#f87171', text: '#991b1b', light: '#fee2e2' },
  indigo: { bg: '#818cf8', text: '#3730a3', light: '#e0e7ff' }
};

// Sample Oplaes data for development
const SAMPLE_OPLAES = [
  {
    id: 'oplae-1',
    name: 'Peak Hours Posting',
    color: 'blue',
    strategy: 'Peak Hours',
    tasks: [
      {
        id: 'task-1',
        title: 'Engage with #growthhacking',
        scheduledFor: new Date(2024, 1, 11, 10, 0).toISOString(),
        type: POST_TYPES.OPLAE_TASK,
        oplaeId: 'oplae-1',
        oplaeColor: 'blue',
        goal: 5,
        complete: 1,
        query: {
          type: 'hashtag',
          value: '#growthhacking'
        }
      },
      {
        id: 'task-2',
        title: 'Watch competitor videos',
        scheduledFor: new Date(2024, 1, 11, 14, 0).toISOString(),
        type: POST_TYPES.OPLAE_TASK,
        oplaeId: 'oplae-1',
        oplaeColor: 'blue',
        goal: 3,
        complete: 0
      }
    ],
    videos: [
      {
        id: 'video-1',
        title: 'Growth Strategy Results',
        scheduledFor: new Date(2024, 1, 12, 15, 0).toISOString(),
        type: POST_TYPES.OPLAE_VIDEO,
        oplaeId: 'oplae-1',
        oplaeColor: 'blue',
        temperature: null,
        stats: null
      }
    ]
  },
  {
    id: 'oplae-2',
    name: 'Weekly Content',
    color: 'purple',
    strategy: 'Engage-Post-Engage',
    tasks: [
      {
        id: 'task-3',
        title: 'Reply to top comments',
        scheduledFor: new Date(2024, 1, 12, 11, 0).toISOString(),
        type: POST_TYPES.OPLAE_TASK,
        oplaeId: 'oplae-2',
        oplaeColor: 'purple',
        goal: 10,
        complete: 4
      }
    ],
    videos: [
      {
        id: 'video-2',
        title: 'Content Strategy Framework',
        scheduledFor: new Date(2024, 1, 13, 16, 0).toISOString(),
        type: POST_TYPES.OPLAE_VIDEO,
        oplaeId: 'oplae-2',
        oplaeColor: 'purple',
        temperature: 0.5,
        stats: {
          comments: 3,
          shares: 78,
          likes: 423
        }
      }
    ]
  }
];

const ANIMATIONS = {
  spring: {
    type: "spring",
    stiffness: 300,
    damping: 30
  },
  fadeIn: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.2 }
  },
  slideIn: {
    initial: { x: -20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 20, opacity: 0 },
    transition: { duration: 0.2 }
  }
};

// iOS-specific breakpoints and constants
const IOS_BREAKPOINTS = {
  iPhone: '390px',    // iPhone 12/13/14
  iPhonePlus: '428px' // iPhone Pro Max
};

const IOS_SAFE_AREAS = {
  top: 'env(safe-area-inset-top, 47px)',      // Default notch height
  bottom: 'env(safe-area-inset-bottom, 34px)', // Default home indicator height
};

// Add navbar height constant to match DashboardPage
const NAVBAR_HEIGHT = '76px';

// Enhanced animations and transitions
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

// Helper function to draw smooth connection curves
const drawConnection = (startX, startY, endX, endY, curveStrength = 100) => {
  const midX = startX + (endX - startX) / 2;
  const controlPoint1X = startX + (midX - startX) * 0.8;
  const controlPoint2X = endX - (endX - midX) * 0.8;
  
  // Add slight vertical offset to control points for smoother S-curve
  const verticalOffset = Math.min(curveStrength, Math.abs(endY - startY) * 0.3);
  const controlPoint1Y = startY + (endY > startY ? verticalOffset : -verticalOffset);
  const controlPoint2Y = endY + (endY > startY ? -verticalOffset : verticalOffset);
  
  return `M ${startX} ${startY} 
          C ${controlPoint1X} ${controlPoint1Y} 
            ${controlPoint2X} ${controlPoint2Y} 
            ${endX} ${endY}`;
};

// First, update the SAMPLE_CAMPAIGN to use dynamic dates relative to current date
const getCurrentWeekDates = () => {
  const now = new Date();
  const sunday = startOfWeek(now);
  return {
    sunday: sunday,
    tuesday: addDays(sunday, 2),
    thursday: addDays(sunday, 4),
    saturday: addDays(sunday, 6),
  };
};

const weekDates = getCurrentWeekDates();

// Update the SAMPLE_CAMPAIGN with multiple campaigns
const SAMPLE_CAMPAIGNS = [
  {
    id: 'campaign-1',
    name: 'Summer Collection Launch',
    color: 'blue',
    posts: [
      {
        id: 'post-1',
        title: 'Teaser Video',
        scheduledFor: new Date(2024, 1, 11, 10, 0).toISOString(),
        type: 'video',
        campaignId: 'campaign-1',
        campaignColor: 'blue'
      },
      {
        id: 'post-2',
        title: 'Product Showcase',
        scheduledFor: new Date(2024, 1, 13, 14, 30).toISOString(),
        type: 'automation',
        campaignId: 'campaign-1',
        campaignColor: 'blue'
      }
    ]
  },
  {
    id: 'campaign-2',
    name: 'Brand Storytelling',
    color: 'purple',
    posts: [
      {
        id: 'post-3',
        title: 'Behind the Scenes',
        scheduledFor: new Date(2024, 1, 12, 11, 0).toISOString(),
        type: 'video',
        campaignId: 'campaign-2',
        campaignColor: 'purple'
      },
      {
        id: 'post-4',
        title: 'Team Spotlight',
        scheduledFor: new Date(2024, 1, 14, 13, 0).toISOString(),
        type: 'video',
        campaignId: 'campaign-2',
        campaignColor: 'purple'
      }
    ]
  },
  {
    id: 'campaign-3',
    name: 'Product Tutorial Series',
    color: 'green',
    posts: [
      {
        id: 'post-5',
        title: 'Quick Tips',
        scheduledFor: new Date(2024, 1, 11, 15, 0).toISOString(),
        type: 'automation',
        campaignId: 'campaign-3',
        campaignColor: 'green'
      },
      {
        id: 'post-6',
        title: 'Advanced Features',
        scheduledFor: new Date(2024, 1, 13, 16, 0).toISOString(),
        type: 'video',
        campaignId: 'campaign-3',
        campaignColor: 'green'
      }
    ]
  },
  {
    id: 'campaign-4',
    name: 'Customer Success Stories',
    color: 'pink',
    posts: [
      {
        id: 'post-7',
        title: 'Testimonial Highlight',
        scheduledFor: new Date(2024, 1, 12, 9, 0).toISOString(),
        type: 'automation',
        campaignId: 'campaign-4',
        campaignColor: 'pink'
      },
      {
        id: 'post-8',
        title: 'Case Study Review',
        scheduledFor: new Date(2024, 1, 14, 10, 30).toISOString(),
        type: 'video',
        campaignId: 'campaign-4',
        campaignColor: 'pink'
      }
    ]
  },
  {
    id: 'campaign-5',
    name: 'Holiday Special',
    color: 'orange',
    posts: [
      {
        id: 'post-9',
        title: 'Seasonal Announcement',
        scheduledFor: new Date(2024, 1, 11, 13, 0).toISOString(),
        type: 'video',
        campaignId: 'campaign-5',
        campaignColor: 'orange'
      },
      {
        id: 'post-10',
        title: 'Gift Guide Preview',
        scheduledFor: new Date(2024, 1, 13, 11, 30).toISOString(),
        type: 'automation',
        campaignId: 'campaign-5',
        campaignColor: 'orange'
      }
    ]
  }
];

// Add after the SAMPLE_CAMPAIGN constant
const recommendations = [
  {
    platform: 'TikTok',
    bestTime: new Date().setHours(12, 0, 0),
    engagementRate: 0.089
  },
  {
    platform: 'Instagram',
    bestTime: new Date().setHours(15, 30, 0),
    engagementRate: 0.076
  },
  {
    platform: 'YouTube',
    bestTime: new Date().setHours(17, 0, 0),
    engagementRate: 0.064
  }
].map(rec => ({
  ...rec,
  bestTime: new Date(rec.bestTime).toISOString()
}));

// Add new campaign list component
const CampaignList = ({ campaigns, activeCampaign, onCampaignClick }) => {
  return (
    <div className="w-64 min-w-64 bg-white/30 backdrop-blur-md border-r border-white/20 p-4">
      <div className="space-y-4">
        {campaigns.map((campaign) => (
          <div 
            key={campaign.id}
            className={cn(
              "relative group cursor-pointer",
              "hover:bg-white/40 rounded-lg transition-all",
              activeCampaign?.id === campaign.id && "bg-white/40"
            )}
            onClick={() => onCampaignClick(campaign)}
          >
            <div className="flex items-center gap-3 p-3">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: CAMPAIGN_COLORS[campaign.color] }}
                />
                <span className="text-sm font-medium text-gray-700">
                  {campaign.name}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button className="p-1 hover:bg-white/40 rounded">
                  <Video className="w-4 h-4 text-gray-500" />
                </button>
                <button className="p-1 hover:bg-white/40 rounded">
                  <Zap className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            {/* Progress indicator */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-100">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                style={{ 
                  width: `${(campaign.posts.filter(p => new Date(p.scheduledFor) <= new Date()).length / campaign.posts.length) * 100}%` 
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CalendarHeader = ({ 
  currentDate, 
  currentView, 
  onDateChange, 
  onViewChange,
  onOpenSettings,
  scheduledContent,
  filters,
  setShowFilters,
  oplaes = [],
  activeOplae,
  setActiveOplae,
  className,
  theme = 'light'
}) => {
  return (
    <motion.div
      {...ANIMATIONS.fadeIn}
      className={cn(
        "sticky top-0 z-50 backdrop-blur-md border-b",
        theme === 'light' 
          ? "bg-white/80 border-gray-200"
          : "bg-gray-800/80 border-gray-700",
        "py-4 px-6",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className={cn(
            "text-2xl font-bold bg-clip-text text-transparent",
            theme === 'light'
              ? "bg-gradient-to-r from-indigo-600 to-purple-600"
              : "bg-gradient-to-r from-indigo-400 to-purple-400"
          )}>
            Content Calendar
          </h1>
          <div className="hidden md:flex items-center gap-2 ml-4">
            <Select
              value={currentView}
              onValueChange={onViewChange}
              options={[
                { label: 'Week', value: CALENDAR_VIEWS.WEEK },
                { label: 'Month', value: CALENDAR_VIEWS.MONTH },
                { label: 'Agenda', value: CALENDAR_VIEWS.AGENDA }
              ]}
              className={cn(
                "w-32",
                theme === 'dark' && "bg-gray-800 border-gray-700 text-gray-200"
              )}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDateChange(addDays(currentDate, -1))}
              className="h-8 px-2"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDateChange(new Date())}
              className={cn(
                "h-8 px-3",
                isToday(currentDate) && "bg-indigo-50 text-indigo-600"
              )}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDateChange(addDays(currentDate, 1))}
              className="h-8 px-2"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenSettings}
              className="h-8 w-8 p-0"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <Badge variant="secondary" className="hidden md:flex">
            {scheduledContent.length} posts
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          <Tooltip content="Filter posts">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(prev => !prev)}
              className={cn(
                "h-8 w-8 p-0",
                filters.postTypes.length > 0 && "text-indigo-600"
              )}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </Tooltip>
          
          {oplaes.length > 0 && (
            <Select
              value={activeOplae?.id || ''}
              onValueChange={(value) => setActiveOplae(
                value ? oplaes.find(c => c.id === value) : null
              )}
              options={[
                { label: 'All Oplaes', value: '' },
                ...oplaes.map(c => ({
                  label: c.name,
                  value: c.id,
                  icon: (
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: CAMPAIGN_COLORS[c.color]?.bg }}
                    />
                  )
                }))
              ]}
              className="w-40"
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CalendarToolbar = ({
  onNewPost,
  onNewAutomation,
  className
}) => {
  return (
    <motion.div
      {...ANIMATIONS.slideIn}
      className={cn(
        "fixed bottom-6 left-1/2 transform -translate-x-1/2",
        "flex items-center gap-2 p-2 rounded-full bg-white/90 backdrop-blur-md shadow-lg border border-gray-200",
        className
      )}
    >
      <Tooltip content="New Video Post">
        <Button
          variant="ghost"
          size="sm"
          onClick={onNewPost}
          className="h-9 w-9 p-0 rounded-full"
        >
          <Video className="h-4 w-4" />
        </Button>
      </Tooltip>
      
      <Tooltip content="New Automation">
        <Button
          variant="ghost"
          size="sm"
          onClick={onNewAutomation}
          className="h-9 w-9 p-0 rounded-full"
        >
          <Zap className="h-4 w-4" />
        </Button>
      </Tooltip>

      <div className="w-px h-6 bg-gray-200" />

      <Tooltip content="View Analytics">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/analytics')}
          className="h-9 w-9 p-0 rounded-full"
        >
          <TrendingUp className="h-4 w-4" />
        </Button>
      </Tooltip>
      
      <Tooltip content="More Options">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSettings(true)}
          className="h-9 w-9 p-0 rounded-full"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </Tooltip>
    </motion.div>
  );
};

const CalendarGrid = ({ 
  currentDate,
  currentView,
  scheduledContent,
  onPostDrag,
  userPreferences,
  activeOplae,
  oplaes,
  setScheduledContent,
  className 
}) => {
  const scrollContainerRef = useRef(null);
  const [connections, setConnections] = useState([]);
  const [draggedPostId, setDraggedPostId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Add handlePostDrag function
  const handlePostDrag = useCallback((postId, offsetY) => {
    setDraggedPostId(postId);
    setDragOffset(prev => ({ ...prev, y: offsetY }));
    updateConnections();
  }, [updateConnections]);

  // Add handleDragEnd function
  const handleDragEnd = useCallback(() => {
    setDraggedPostId(null);
    setDragOffset({ x: 0, y: 0 });
    updateConnections();
  }, [updateConnections]);

  const updateConnections = useCallback(() => {
    if (currentView === CALENDAR_VIEWS.WEEK && scrollContainerRef.current) {
      const oplaeItems = {};
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const scrollTop = scrollContainerRef.current.scrollTop;
      
      // Group items by Oplae
      scheduledContent.forEach(item => {
        if (item.oplaeId) {
          if (!oplaeItems[item.oplaeId]) {
            oplaeItems[item.oplaeId] = {
              tasks: [],
              videos: []
            };
          }
          if (item.type === POST_TYPES.OPLAE_TASK) {
            oplaeItems[item.oplaeId].tasks.push(item);
          } else if (item.type === POST_TYPES.OPLAE_VIDEO) {
            oplaeItems[item.oplaeId].videos.push(item);
          }
        }
      });

      const newConnections = [];

      // Create connections for each Oplae
      Object.entries(oplaeItems).forEach(([oplaeId, items]) => {
        // Sort all items by scheduled time
        const allItems = [...items.tasks, ...items.videos].sort((a, b) => 
          new Date(a.scheduledFor) - new Date(b.scheduledFor)
        );
        
        // Create connections between consecutive items
        for (let i = 0; i < allItems.length - 1; i++) {
          const currentItem = document.querySelector(`[data-post-id="${allItems[i].id}"]`);
          const nextItem = document.querySelector(`[data-post-id="${allItems[i + 1].id}"]`);
          
          if (currentItem && nextItem) {
            const currentRect = currentItem.getBoundingClientRect();
            const nextRect = nextItem.getBoundingClientRect();

            // Calculate positions relative to the scroll container
            const startX = currentRect.right - containerRect.left + scrollLeft;
            const startY = currentRect.top + (currentRect.height / 2) - containerRect.top + scrollTop;
            const endX = nextRect.left - containerRect.left + scrollLeft;
            const endY = nextRect.top + (nextRect.height / 2) - containerRect.top + scrollTop;

            // Only add connection if items are visible in the viewport
            const isVisible = (
              startY >= scrollTop && 
              startY <= scrollTop + containerRect.height &&
              endY >= scrollTop && 
              endY <= scrollTop + containerRect.height
            );

            if (isVisible) {
              const oplaeColor = CAMPAIGN_COLORS[allItems[i].oplaeColor || 'blue'].bg;
              const isDifferentTypes = allItems[i].type !== allItems[i + 1].type;
              
              // Create a more pronounced curve for task-to-video connections
              const path = isDifferentTypes 
                ? drawConnection(
                    startX, 
                    startY, 
                    endX, 
                    endY, 
                    Math.abs(endX - startX) * 0.6  // Increase curve strength for different types
                  )
                : drawConnection(
                    startX, 
                    startY, 
                    endX, 
                    endY, 
                    Math.abs(endX - startX) * 0.4  // Normal curve for same types
                  );
              
              newConnections.push({
                id: `${allItems[i].id}-${allItems[i + 1].id}`,
                path,
                color: oplaeColor,
                type: isDifferentTypes ? 'dashed' : 'solid',
                order: isDifferentTypes ? 1 : 0, // Render solid lines first
                opacity: isDifferentTypes ? 0.6 : 0.8 // Slightly more transparent for dashed lines
              });
            }
          }
        }
      });

      // Sort connections to render solid lines first, then dashed lines
      newConnections.sort((a, b) => a.order - b.order);
      setConnections(newConnections);
    }
  }, [scheduledContent, currentView, draggedPostId, dragOffset]);

  // Update connections when content changes or on scroll with debounce
  useEffect(() => {
    const container = scrollContainerRef.current;
    let scrollTimeout;
    
    const handleScroll = () => {
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = window.requestAnimationFrame(() => {
        updateConnections();
      });
    };
    
    updateConnections();
    
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
    };
  }, [scheduledContent, currentView, activeOplae, updateConnections]);

  const visibleDays = useMemo(() => {
    if (currentView === CALENDAR_VIEWS.MONTH) {
      // For month view, show 3 months (previous, current, next)
      const start = startOfWeek(startOfMonth(addMonths(currentDate, -1)));
      const end = endOfWeek(endOfMonth(addMonths(currentDate, 1)));
      return eachDayOfInterval({ start, end });
    } else {
      // For week view, show 3 weeks (previous, current, next)
      const start = startOfWeek(addWeeks(currentDate, -1));
      const end = endOfWeek(addWeeks(currentDate, 1));
      return eachDayOfInterval({ start, end });
    }
  }, [currentDate, currentView]);

  return (
    <div 
      className={cn(
        "relative flex-1 overflow-hidden rounded-xl shadow-xl backdrop-blur-md",
        userPreferences.theme === 'light' 
          ? "bg-white/60 shadow-black/5"
          : "bg-gray-800/40 shadow-black/20",
        className
      )}
    >
      {/* Time column - Only show in week view */}
      {currentView === CALENDAR_VIEWS.WEEK && (
        <div className={cn(
          "absolute left-0 top-0 bottom-0 w-16 backdrop-blur-md z-20 border-r",
          userPreferences.theme === 'light' 
            ? "bg-white/60 border-gray-200/20"
            : "bg-gray-800/60 border-gray-700/20"
        )}>
          <div className="h-14" />
          {TIME_SLOTS.map((hour) => (
            <div 
              key={hour}
              className="h-14 flex items-center justify-center"
            >
              <span className={cn(
                "text-xs font-medium",
                userPreferences.theme === 'light' ? "text-gray-400" : "text-gray-500"
              )}>
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Scrollable container */}
      <div 
        ref={scrollContainerRef}
        className={cn(
          "overflow-x-auto hide-scrollbar relative",
          currentView === CALENDAR_VIEWS.WEEK ? "ml-16" : ""
        )}
      >
        {/* Campaign connections SVG layer */}
        {currentView === CALENDAR_VIEWS.WEEK && (
          <svg 
            className="absolute inset-0 pointer-events-none"
            style={{ 
              width: '100%', 
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              position: 'absolute',
              overflow: 'visible'
            }}
          >
            <defs>
              <filter id="connection-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                <feOffset dx="0" dy="1" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.3" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <pattern id="dashed-pattern" x="0" y="0" width="8" height="1" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="4" y2="0" stroke="currentColor" strokeWidth="2" />
              </pattern>
            </defs>
            {connections.map(connection => (
              <g key={connection.id}>
                {/* Background glow */}
                <path
                  d={connection.path}
                  stroke={connection.color}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  style={{
                    opacity: connection.opacity,
                    transition: 'all 75ms linear'
                  }}
                />
                {/* Main line */}
                <path
                  d={connection.path}
                  stroke={connection.type === 'dashed' ? `url(#dashed-pattern) ${connection.color}` : connection.color}
                  strokeWidth="2"
                  strokeDasharray={connection.type === 'dashed' ? '4 4' : 'none'}
                  fill="none"
                  style={{
                    filter: 'url(#connection-shadow)',
                    opacity: 0.8,
                    transition: 'all 75ms linear'
                  }}
                />
              </g>
            ))}
          </svg>
        )}

        {/* Calendar grid */}
        <div className={cn(
          "inline-flex",
          currentView === CALENDAR_VIEWS.MONTH ? "flex-wrap" : "flex-nowrap",
          currentView === CALENDAR_VIEWS.MONTH ? "min-w-full" : ""
        )}>
          {visibleDays.map((day, index) => {
            const dayItems = getScheduledItemsForDay(day, scheduledContent, activeOplae, oplaes);
            const isCurrentMonth = isSameMonth(day, currentDate);
            
            return (
              <div 
                key={day.toString()}
                className={cn(
                  "relative",
                  currentView === CALENDAR_VIEWS.WEEK ? "min-w-[200px]" : "w-[14.28%]",
                  !userPreferences?.showWeekends && (day.getDay() === 0 || day.getDay() === 6) && "hidden",
                  !isCurrentMonth && "opacity-50"
                )}
              >
                {/* Day header */}
                <div className={cn(
                  "sticky top-0 backdrop-blur-md z-10 p-3",
                  userPreferences.theme === 'light' 
                    ? "bg-white/60"
                    : "bg-gray-800/60"
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={cn(
                        "text-sm font-medium",
                        userPreferences.theme === 'light' ? "text-gray-400" : "text-gray-500"
                      )}>
                        {format(day, 'EEE')}
                      </div>
                      <div className={cn(
                        "text-2xl font-bold",
                        isToday(day) 
                          ? userPreferences.theme === 'light' ? "text-indigo-600" : "text-indigo-400"
                          : userPreferences.theme === 'light' ? "text-gray-900" : "text-gray-100",
                        !isCurrentMonth && userPreferences.theme === 'light' ? "text-gray-400" : "text-gray-600"
                      )}>
                        {format(day, 'd')}
                      </div>
                    </div>
                    {isToday(day) && (
                      <Badge 
                        variant="secondary" 
                        className={cn(
                          userPreferences.theme === 'light'
                            ? "bg-indigo-50 text-indigo-600"
                            : "bg-indigo-900/50 text-indigo-400"
                        )}
                      >
                        Today
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Time grid or day content */}
                <div className="relative">
                  {currentView === CALENDAR_VIEWS.WEEK ? (
                    // Week view - show time slots and posts
                    <>
                      {/* Time slots background - invisible but maintains structure */}
                      {TIME_SLOTS.map((hour) => (
                        <div 
                          key={hour}
                          className="h-14 group"
                        >
                          <Button
                            variant="ghost"
                            className="w-full h-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              const date = new Date(day);
                              date.setHours(hour);
                              onPostDrag(date);
                            }}
                          >
                            <Plus className="h-3 w-3 text-gray-400" />
                          </Button>
                        </div>
                      ))}

                      {/* Posts */}
                      {dayItems.map((item) => {
                        const itemDate = parseISO(item.scheduledFor);
                        const hour = itemDate.getHours();
                        const minutes = itemDate.getMinutes();
                        const topOffset = ((hour - 8) * 56) + (minutes / 60) * 56;
                        
                        return (
                          <div
                            key={item.id}
                            style={{
                              position: 'absolute',
                              top: `${topOffset + 56}px`,
                              left: '8px',
                              right: '8px',
                              zIndex: 15
                            }}
                          >
                            {renderPost(item, day, handlePostDrag, handleDragEnd, onPostDrag, userPreferences)}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    // Month view - show condensed day content
                    <div className="min-h-[100px] p-2">
                      {dayItems.slice(0, 3).map((item) => (
                        <div
                          key={item.id}
                          className="mb-1 p-1 text-xs rounded bg-white/60 backdrop-blur-sm border border-white/20"
                        >
                          <div className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ 
                                backgroundColor: item.campaignId ? 
                                  CAMPAIGN_COLORS[item.campaignColor || 'blue'].bg : 
                                  '#e5e7eb' 
                              }}
                            />
                            <span className="truncate">{item.title}</span>
                          </div>
                        </div>
                      ))}
                      {dayItems.length > 3 && (
                        <div className="text-xs text-gray-500 mt-1">
                          +{dayItems.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const renderPost = (item, dayElement, onDrag, onDragEnd, onPostDrag, userPreferences) => {
  const oplaeColor = item.oplaeColor ? CAMPAIGN_COLORS[item.oplaeColor] : null;
  const dragControls = useDragControls();
  const [isHovered, setIsHovered] = useState(false);
  
  // Special styling for placeholder posts
  if (item.isPlaceholder) {
    return (
      <div
        className={cn(
          "group relative flex items-center gap-2 p-2 rounded-lg",
          "border border-dashed border-gray-300",
          "transition-all duration-200"
        )}
        style={{
          backgroundColor: 'rgba(255,255,255,0.4)'
        }}
        data-post-id={item.id}
      >
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <Plus className="h-3 w-3 text-gray-400" />
          <span className="block truncate text-xs font-medium text-gray-400">
            {item.title}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-400">
            {format(parseISO(item.scheduledFor), 'HH:mm')}
          </span>
          <Badge variant="secondary" className="bg-gray-50 text-gray-400 text-[10px] px-1.5 py-0.5">
            <Video className="h-2.5 w-2.5 mr-0.5" />
            Video
          </Badge>
        </div>
      </div>
    );
  }

  // Special styling for Oplae tasks
  if (item.type === POST_TYPES.OPLAE_TASK) {
    return (
      <motion.div
        layout
        drag="y"
        dragControls={dragControls}
        dragConstraints={dayElement}
        dragElastic={0.1}
        dragMomentum={false}
        whileHover={{ scale: 1.02, y: -1 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={cn(
          "group relative flex items-center gap-2 p-2 rounded-lg transition-all duration-200",
          "backdrop-blur-sm border shadow-sm hover:shadow-md",
          "border-l-4",
          userPreferences.theme === 'light'
            ? ["bg-white/60 hover:bg-white/80", "shadow-black/5 hover:shadow-black/10"]
            : ["bg-gray-800/40 hover:bg-gray-800/60", "shadow-black/20 hover:shadow-black/30"]
        )}
        style={{
          borderColor: `${oplaeColor?.bg}40`,
          borderLeftColor: oplaeColor?.bg,
          backgroundColor: `${oplaeColor?.light}20`
        }}
        data-post-id={item.id}
        data-oplae-id={item.oplaeId}
      >
        <div 
          className="absolute -left-1 -top-1 w-2 h-2 rounded-full ring-1 ring-white"
          style={{ backgroundColor: oplaeColor?.bg }}
        />
        
        <GripVertical 
          className="h-3 w-3 text-gray-400 cursor-grab"
          onPointerDown={(e) => dragControls.start(e)}
        />
        
        <div className="flex-1 min-w-0">
          <span className="block truncate text-xs font-medium text-gray-700">
            {item.title}
          </span>
          {item.goal && (
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                  style={{ width: `${(item.complete / item.goal) * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-500">
                {item.complete}/{item.goal}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-500">
            {format(parseISO(item.scheduledFor), 'HH:mm')}
          </span>
          <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 text-[10px] px-1.5 py-0.5">
            <Target className="h-2.5 w-2.5 mr-0.5" />
            Task
          </Badge>
        </div>
      </motion.div>
    );
  }

  // Special styling for Oplae videos
  if (item.type === POST_TYPES.OPLAE_VIDEO) {
    return (
      <motion.div
        layout
        drag="y"
        dragControls={dragControls}
        dragConstraints={dayElement}
        dragElastic={0.1}
        dragMomentum={false}
        whileHover={{ scale: 1.02, y: -1 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={cn(
          "group relative flex items-center gap-2 p-2 rounded-lg transition-all duration-200",
          "backdrop-blur-sm border shadow-sm hover:shadow-md",
          "border-l-4",
          userPreferences.theme === 'light'
            ? ["bg-white/60 hover:bg-white/80", "shadow-black/5 hover:shadow-black/10"]
            : ["bg-gray-800/40 hover:bg-gray-800/60", "shadow-black/20 hover:shadow-black/30"]
        )}
        style={{
          borderColor: `${oplaeColor?.bg}40`,
          borderLeftColor: oplaeColor?.bg,
          backgroundColor: `${oplaeColor?.light}20`
        }}
        data-post-id={item.id}
        data-oplae-id={item.oplaeId}
      >
        <div 
          className="absolute -left-1 -top-1 w-2 h-2 rounded-full ring-1 ring-white"
          style={{ backgroundColor: oplaeColor?.bg }}
        />
        
        <GripVertical 
          className="h-3 w-3 text-gray-400 cursor-grab"
          onPointerDown={(e) => dragControls.start(e)}
        />
        
        <div className="flex-1 min-w-0">
          <span className="block truncate text-xs font-medium text-gray-700">
            {item.title}
          </span>
          {item.stats && (
            <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
              <span>{item.stats.likes} likes</span>
              <span>•</span>
              <span>{item.stats.comments} comments</span>
              <span>•</span>
              <span>{item.stats.shares} shares</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-500">
            {format(parseISO(item.scheduledFor), 'HH:mm')}
          </span>
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5">
            <Video className="h-2.5 w-2.5 mr-0.5" />
            Video
          </Badge>
        </div>
      </motion.div>
    );
  }

  // ... existing renderPost code for other types ...
};

const getScheduledItemsForDay = (date, scheduledContent, activeOplae, oplaes) => {
  // Start with all scheduled content
  let allItems = [...scheduledContent];
  
  // Add Oplae tasks and videos
  if (activeOplae) {
    // If there's an active Oplae, only show its tasks and videos
    const oplae = oplaes.find(o => o.id === activeOplae.id);
    if (oplae) {
      allItems = [
        ...oplae.tasks.map(task => ({
          ...task,
          oplaeId: oplae.id,
          oplaeColor: oplae.color
        })),
        ...oplae.videos.map(video => ({
          ...video,
          oplaeId: oplae.id,
          oplaeColor: oplae.color
        }))
      ];
    }
  } else if (oplaes?.length > 0) {
    // If no active Oplae, show all tasks and videos from all Oplaes
    allItems = oplaes.flatMap(oplae => [
      ...oplae.tasks.map(task => ({
        ...task,
        oplaeId: oplae.id,
        oplaeColor: oplae.color
      })),
      ...oplae.videos.map(video => ({
        ...video,
        oplaeId: oplae.id,
        oplaeColor: oplae.color
      }))
    ]);
  }
  
  // Filter items for the specific day and sort by time
  let filteredItems = allItems
    .filter(item => {
      const scheduleDate = parseISO(item.scheduledFor);
      return isSameDay(scheduleDate, date);
    })
    .sort((a, b) => {
      const timeA = parseISO(a.scheduledFor);
      const timeB = parseISO(b.scheduledFor);
      return timeA.getTime() - timeB.getTime();
    });

  // Add placeholder if no items and it's a future date
  if (filteredItems.length === 0 && date > new Date()) {
    const placeholderDate = new Date(date);
    placeholderDate.setHours(12, 0, 0);
    
    filteredItems = [{
      id: `placeholder-${date.toISOString()}`,
      title: 'Schedule content',
      scheduledFor: placeholderDate.toISOString(),
      type: 'placeholder',
      isPlaceholder: true
    }];
  }

  return filteredItems;
};

const useKeyboardShortcuts = (handlers) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Navigation shortcuts
      if (e.metaKey || e.ctrlKey) {
        switch (e.key) {
          case 'ArrowLeft':
            handlers.onPrevDay?.();
            break;
          case 'ArrowRight':
            handlers.onNextDay?.();
            break;
          case 'n':
            handlers.onNewPost?.();
            break;
          case ',':
            handlers.onOpenSettings?.();
            break;
          default:
            return;
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlers]);
};

// EmptyState component
const EmptyState = ({ navigate, theme }) => (
  <div className="text-center py-8 px-4">
    <div className="max-w-md mx-auto">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
        <Sparkles className="w-10 h-10 text-indigo-600" />
      </div>
      <h2 className="text-2xl font-semibold mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text">
        Your Content Calendar is Empty
      </h2>
      <p className="text-gray-600 mb-8">
        Start scheduling your content to make the most of your social media presence!
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          onClick={() => navigate('/automation')}
          className={cn(
            "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center gap-2",
            theme === 'dark' && "bg-gray-800 hover:bg-gray-700"
          )}
        >
          <Zap className="w-4 h-4" />
          Set Up Automation
          <ArrowRight className="w-4 h-4" />
        </Button>
        <Button
          onClick={() => navigate('/cross-post')}
          variant="outline"
          className={cn(
            "border-indigo-200 hover:border-indigo-300 flex items-center gap-2",
            theme === 'dark' && "bg-gray-800 hover:bg-gray-700"
          )}
        >
          <Video className="w-4 h-4" />
          Schedule Cross-Posts
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

// Main CalendarPage component
const CalendarPage = () => {
  const navigate = useNavigate();
  
  // State management
  const [currentDate, setCurrentDate] = useState(new Date(2024, 1, 11));
  const [currentView, setCurrentView] = useState(CALENDAR_VIEWS.WEEK);
  const [scheduledContent, setScheduledContent] = useState([]);
  const [oplaes, setOplaes] = useState(SAMPLE_OPLAES);
  const [activeOplae, setActiveOplae] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    postTypes: [],
    oplaes: [],
    dateRange: null
  });
  const [showSettings, setShowSettings] = useState(false);
  const [userPreferences, setUserPreferences] = useState({
    defaultView: 'week',
    showWeekends: true,
    workingHours: {
      start: 8,
      end: 20
    },
    notifications: true,
    theme: 'dark'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Combine all tasks and videos from Oplaes
  const allScheduledContent = useMemo(() => {
    return oplaes.flatMap(oplae => [
      ...oplae.tasks.map(task => ({
        ...task,
        oplaeId: oplae.id,
        oplaeColor: oplae.color
      })),
      ...oplae.videos.map(video => ({
        ...video,
        oplaeId: oplae.id,
        oplaeColor: oplae.color
      }))
    ]);
  }, [oplaes]);

  // Filtered content based on active Oplae and filters
  const filteredContent = useMemo(() => {
    let items = allScheduledContent;

    if (activeOplae) {
      items = items.filter(item => item.oplaeId === activeOplae.id);
    }

    if (filters.postTypes.length) {
      items = items.filter(item => filters.postTypes.includes(item.type));
    }

    if (filters.oplaes.length) {
      items = items.filter(item => filters.oplaes.includes(item.oplaeId));
    }

    return items;
  }, [allScheduledContent, activeOplae, filters]);

  // Callbacks
  const handleDateChange = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    setUserPreferences(prev => ({ ...prev, defaultView: view }));
  }, []);

  const handlePostDrag = useCallback((post, newDate) => {
    setScheduledContent(prev => 
      prev.map(item => 
        item.id === post.id 
          ? { ...item, scheduledFor: newDate.toISOString() }
          : item
      )
    );
    
    // Update backend
    try {
      sendRequest(`/cross-posts/${post.id}`, 'PATCH', {
        scheduledFor: newDate.toISOString()
      });
    } catch (error) {
      console.error('Failed to update post schedule:', error);
    }
  }, []);

  // Update useEffect for fetchScheduledContent
  useEffect(() => {
    const fetchOplaes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const startDate = startOfMonth(currentDate);
        const endDate = endOfMonth(currentDate);
        
        try {
          const oplaesResponse = await sendRequest(
            `/oplaes/schedule?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
            'GET'
          );

          if (oplaesResponse?.oplaes) {
            setOplaes(oplaesResponse.oplaes);
          }
        } catch (apiError) {
          console.warn('API request failed, using sample data:', apiError);
          setOplaes(SAMPLE_OPLAES);
        }
      } catch (error) {
        console.error('Failed to fetch Oplaes:', error);
        setError('Failed to load your schedule. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOplaes();
  }, [currentDate]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onPrevDay: () => handleDateChange(addDays(currentDate, -1)),
    onNextDay: () => handleDateChange(addDays(currentDate, 1)),
    onOpenSettings: () => setShowSettings(true)
  });

  return (
    <div className={cn(
      "min-h-screen",
      userPreferences.theme === 'light' 
        ? "bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50"
        : "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900",
      "pt-[var(--navbar-height,_76px)]",
      "pb-[calc(env(safe-area-inset-bottom,_34px)_+_1rem)]",
      userPreferences.theme === 'dark' && "text-gray-100"
    )}>
      <div className="h-[calc(100vh-76px)] flex">
        {/* Left sidebar - Oplaes list */}
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className={cn(
            "w-64 min-w-64 overflow-y-auto border-r",
            userPreferences.theme === 'light'
              ? "bg-white/60 backdrop-blur-md border-white/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)]"
              : "bg-gray-800/40 backdrop-blur-md border-gray-700/20 shadow-[0_4px_30px_rgba(0,0,0,0.3)]"
          )}
        >
          <div className="p-4">
            <h2 className={cn(
              "text-lg font-semibold mb-4",
              userPreferences.theme === 'dark' && "text-gray-100"
            )}>
              Active Oplaes
            </h2>
            <div className="space-y-2">
              {oplaes.map((oplae) => (
                <button
                  key={oplae.id}
                  onClick={() => setActiveOplae(oplae)}
                  className={cn(
                    "w-full p-3 rounded-lg text-left transition-all",
                    userPreferences.theme === 'light'
                      ? [
                        "hover:bg-white/40",
                        activeOplae?.id === oplae.id && "bg-white/40"
                      ]
                      : [
                        "hover:bg-gray-700/40",
                        activeOplae?.id === oplae.id && "bg-gray-700/40"
                      ]
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: CAMPAIGN_COLORS[oplae.color].bg }}
                    />
                    <span className={cn(
                      "text-sm font-medium",
                      userPreferences.theme === 'light' ? "text-gray-700" : "text-gray-200"
                    )}>
                      {oplae.name}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="text-xs text-gray-500">
                      {oplae.strategy}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                          style={{ 
                            width: `${((oplae.tasks.reduce((sum, task) => sum + task.complete, 0) / 
                              oplae.tasks.reduce((sum, task) => sum + task.goal, 0)) * 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {oplae.tasks.length + oplae.videos.length} items
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Header */}
          <CalendarHeader
            currentDate={currentDate}
            currentView={currentView}
            onDateChange={handleDateChange}
            onViewChange={handleViewChange}
            onOpenSettings={() => setShowSettings(true)}
            scheduledContent={filteredContent}
            filters={filters}
            setShowFilters={setShowFilters}
            oplaes={oplaes}
            activeOplae={activeOplae}
            setActiveOplae={setActiveOplae}
            theme={userPreferences.theme}
          />

          {/* Calendar */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className={cn(
                "p-4 rounded-full bg-gradient-to-r shadow-lg backdrop-blur-sm",
                userPreferences.theme === 'light'
                  ? "from-indigo-500/80 to-purple-500/80 shadow-indigo-500/20"
                  : "from-indigo-600/80 to-purple-600/80 shadow-purple-900/30"
              )}>
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent" />
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className={cn(
                "text-center p-8 rounded-xl backdrop-blur-md shadow-xl",
                userPreferences.theme === 'light'
                  ? "bg-white/60 shadow-black/5"
                  : "bg-gray-800/60 shadow-black/20"
              )}>
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                <h3 className={cn(
                  "text-lg font-semibold mb-2",
                  userPreferences.theme === 'light' ? "text-gray-900" : "text-gray-100"
                )}>
                  Something went wrong
                </h3>
                <p className={cn(
                  "mb-4",
                  userPreferences.theme === 'light' ? "text-gray-600" : "text-gray-400"
                )}>
                  {error}
                </p>
                <Button 
                  onClick={() => window.location.reload()}
                  theme={userPreferences.theme}
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : filteredContent.length === 0 ? (
            <EmptyState navigate={navigate} theme={userPreferences.theme} />
          ) : (
            <CalendarGrid
              currentDate={currentDate}
              currentView={currentView}
              scheduledContent={filteredContent}
              onPostDrag={handlePostDrag}
              userPreferences={userPreferences}
              activeOplae={activeOplae}
              oplaes={oplaes}
              setScheduledContent={setScheduledContent}
              className="flex-1 m-4"
            />
          )}
        </div>
      </div>

      {/* Floating toolbar */}
      <div className={cn(
        "fixed bottom-6 left-1/2 transform -translate-x-1/2",
        "p-2 rounded-full shadow-xl backdrop-blur-md border",
        userPreferences.theme === 'light'
          ? "bg-white/80 border-white/20 shadow-black/5"
          : "bg-gray-800/80 border-gray-700/20 shadow-black/20"
      )}>
        <CalendarToolbar
          onNewPost={() => navigate('/cross-post')}
          onNewAutomation={() => navigate('/automation')}
          theme={userPreferences.theme}
        />
      </div>

      {/* Settings dialog */}
      <Modal 
        open={showSettings} 
        onClose={() => setShowSettings(false)}
        title="Calendar Settings"
        theme={userPreferences.theme}
      >
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className={cn(
              "font-medium",
              userPreferences.theme === 'light' ? "text-gray-900" : "text-gray-100"
            )}>
              View Options
            </h3>
            <div className="flex items-center justify-between">
              <label className={cn(
                "text-sm",
                userPreferences.theme === 'light' ? "text-gray-700" : "text-gray-300"
              )}>
                Default View
              </label>
              <Select
                value={userPreferences.defaultView}
                onValueChange={(value) => setUserPreferences(prev => ({
                  ...prev,
                  defaultView: value
                }))}
                options={[
                  { label: 'Week', value: CALENDAR_VIEWS.WEEK },
                  { label: 'Month', value: CALENDAR_VIEWS.MONTH },
                  { label: 'Agenda', value: CALENDAR_VIEWS.AGENDA }
                ]}
                className="w-32"
                theme={userPreferences.theme}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className={cn(
              "font-medium",
              userPreferences.theme === 'light' ? "text-gray-900" : "text-gray-100"
            )}>
              Show Weekends
            </h3>
            <div className="flex items-center justify-between">
              <label className={cn(
                "text-sm",
                userPreferences.theme === 'light' ? "text-gray-700" : "text-gray-300"
              )}>
                Show Weekends
              </label>
              <Switch
                checked={userPreferences.showWeekends}
                onCheckedChange={(checked) => setUserPreferences(prev => ({
                  ...prev,
                  showWeekends: checked
                }))}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className={cn(
              "font-medium",
              userPreferences.theme === 'light' ? "text-gray-900" : "text-gray-100"
            )}>
              Theme
            </h3>
            <div className="flex items-center justify-between">
              <label className={cn(
                "text-sm",
                userPreferences.theme === 'light' ? "text-gray-700" : "text-gray-300"
              )}>
                Dark Mode
              </label>
              <Switch
                checked={userPreferences.theme === 'dark'}
                onCheckedChange={(checked) => setUserPreferences(prev => ({
                  ...prev,
                  theme: checked ? 'dark' : 'light'
                }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowSettings(false)}
              theme={userPreferences.theme}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // TODO: Save preferences to backend
                setShowSettings(false);
              }}
              theme={userPreferences.theme}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarPage; 